<script setup>
/**
 * Add Members to Chat Room - AI Focus
 * Página para adicionar membros à sala de chat (Passo 2 do wizard)
 */
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const roomId = computed(() => Number(route.params.roomId));
const room = computed(() =>
  store.getters['chatRooms/getChatRoom'](roomId.value)
);
const agents = computed(() => store.getters['agents/getAgents']);
const currentMembers = computed(() => room.value?.members || []);

const selectedAgents = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);

const steps = computed(() => [
  {
    number: 1,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_1_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_1_DESC'),
  },
  {
    number: 2,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_2_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_2_DESC'),
  },
  {
    number: 3,
    title: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_3_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.CREATE.STEP_3_DESC'),
  },
]);

const currentStep = 2;

const availableAgents = computed(() => {
  const memberIds = currentMembers.value.map(m => m.id);
  return agents.value.filter(agent => !memberIds.includes(agent.id));
});

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('chatRooms/show', roomId.value),
      store.dispatch('chatRooms/getMembers', roomId.value),
    ]);
  } catch {
    // Silently fail
  } finally {
    isLoading.value = false;
  }
});

const toggleAgent = agentId => {
  const index = selectedAgents.value.indexOf(agentId);
  if (index === -1) {
    selectedAgents.value.push(agentId);
  } else {
    selectedAgents.value.splice(index, 1);
  }
};

const isSelected = agentId => {
  return selectedAgents.value.includes(agentId);
};

const addMembers = async () => {
  if (selectedAgents.value.length === 0) {
    useAlert(t('CHAT_ROOMS_SETTINGS.MEMBERS.SELECT_AT_LEAST_ONE'));
    return;
  }

  isSaving.value = true;
  try {
    await store.dispatch('chatRooms/addMembers', {
      roomId: roomId.value,
      memberIds: selectedAgents.value,
    });
    useAlert(t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_SUCCESS'));
    router.push({
      name: 'settings_chat_rooms_finish',
      params: { roomId: roomId.value },
    });
  } catch {
    useAlert(t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const skipStep = () => {
  router.push({
    name: 'settings_chat_rooms_finish',
    params: { roomId: roomId.value },
  });
};
</script>

<template>
  <div class="flex h-full overflow-auto p-6">
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner />
    </div>
    <div
      v-else
      class="flex w-full max-w-5xl mx-auto border border-n-weak rounded-xl bg-n-solid-2"
    >
      <!-- Left side - Steps -->
      <div class="w-64 flex-shrink-0 p-6 border-r border-n-weak">
        <div class="relative">
          <!-- Connecting line -->
          <div class="absolute left-4 top-8 w-0.5 bg-n-weak step-line" />

          <div class="space-y-6 relative">
            <div v-for="step in steps" :key="step.number" class="flex gap-3">
              <div
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium flex-shrink-0 z-10"
                :class="
                  step.number === currentStep
                    ? 'bg-woot-500 text-white'
                    : step.number < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-n-solid-3 border border-n-weak text-n-slate-11'
                "
              >
                <fluent-icon
                  v-if="step.number < currentStep"
                  icon="checkmark"
                  size="16"
                />
                <span v-else>{{ step.number }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium"
                  :class="
                    step.number === currentStep
                      ? 'text-woot-500'
                      : step.number < currentStep
                        ? 'text-green-500'
                        : 'text-n-slate-11'
                  "
                >
                  {{ step.title }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ step.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Members selection -->
      <div class="flex-1 p-6 overflow-auto">
        <div class="max-w-lg">
          <h2 class="text-xl font-semibold text-n-slate-12 mb-1">
            {{ t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-6">
            {{ t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_DESC') }}
          </p>

          <div v-if="availableAgents.length === 0" class="text-center py-8">
            <p class="text-n-slate-11">
              {{ $t('CHAT_ROOMS_SETTINGS.MEMBERS.NO_AGENTS_AVAILABLE') }}
            </p>
          </div>

          <div v-else class="space-y-2 mb-6 max-h-96 overflow-y-auto">
            <div
              v-for="agent in availableAgents"
              :key="agent.id"
              class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
              :class="{
                'border-woot-500 bg-woot-50 dark:bg-woot-900/20': isSelected(
                  agent.id
                ),
                'border-n-weak hover:bg-n-alpha-1': !isSelected(agent.id),
              }"
              @click="toggleAgent(agent.id)"
            >
              <input
                type="checkbox"
                :checked="isSelected(agent.id)"
                class="pointer-events-none accent-woot-500"
              />
              <Avatar :src="agent.avatar_url" :name="agent.name" :size="40" />
              <div class="flex-1 min-w-0">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ agent.name }}
                </p>
                <p class="text-sm text-n-slate-10 truncate">
                  {{ agent.email }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="flex items-center justify-between gap-4 pt-4 border-t border-n-weak"
          >
            <Button
              :label="$t('CHAT_ROOMS_SETTINGS.MEMBERS.SKIP')"
              slate
              faded
              @click="skipStep"
            />
            <Button
              :label="$t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_BUTTON')"
              :disabled="selectedAgents.length === 0"
              :is-loading="isSaving"
              @click="addMembers"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.step-line {
  height: calc(100% - 2rem);
}
</style>
